import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { verifyLogin } from '../service/memberService';
import { saveFcmToken, removeFcmToken } from '../service/fcmTokenService';
import { isInRnWebView, onPushToken, requestPushToken } from '../bridge/webviewBridge';
import { registerWebPush } from '../service/webPushService';
import { startNotificationToastListener, stopNotificationToastListener } from '../service/notificationToastService';
import { reportDeviceAccess } from '../service/deviceAccessService';
import { checkIpAccess } from '../service/ipGuard';

const AuthContext = createContext(null);

const STORAGE_KEY = 'plhouse.auth.user';
const ADMIN_STORAGE_KEY = 'plhouse.auth.admin';

// 관리자 계정(대표/admin/isAdmin)은 IP 접속 제한 면제 대상
function isAdminAccount(u) {
  return !!(u && (u.isAdmin || u.dept === '대표' || u.loginId === 'admin'));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const pendingTokenRef = useRef(null); // userData 로드 전 도착한 토큰 보관

  const isAdminPath = window.location.pathname.startsWith('/admin');
  const storageKey = isAdminPath ? ADMIN_STORAGE_KEY : STORAGE_KEY;

  // localStorage에서 세션 복원
  useEffect(() => {
    let alive = true;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const u = JSON.parse(saved);
        setUser(u);
        // 비관리자 세션 복원 시 IP 재확인 → 허용 IP(사무실 등) 밖이면 자동 로그아웃.
        // (관리자는 IP 무관 허용 / localhost·모바일앱·조회실패는 checkIpAccess가 허용 처리)
        if (!isAdminAccount(u)) {
          checkIpAccess(u).then((res) => {
            if (alive && !res.allowed) {
              setUser(null);
              localStorage.removeItem(STORAGE_KEY);
              localStorage.removeItem(ADMIN_STORAGE_KEY);
            }
          }).catch(() => {});
        }
      }
    } catch { /* ignore */ }
    setLoading(false);
    return () => { alive = false; };
  }, [storageKey]);

  // RN에서 FCM 토큰 수신 리스너
  const saveTokenForUser = useCallback(async (token, platform) => {
    const uid = user?.mcode;
    if (!uid) {
      // 아직 로그인 안 됨 → 보류
      pendingTokenRef.current = { token, platform };
      return;
    }
    try {
      await saveFcmToken(uid, token, platform);
      pendingTokenRef.current = null;
    } catch (e) {
      console.error('[FCM] 토큰 저장 실패:', e);
    }
  }, [user?.mcode]);

  // RN WebView일 때 토큰 리스너 등록
  useEffect(() => {
    if (!isInRnWebView()) return;
    const off = onPushToken(saveTokenForUser);
    return () => off();
  }, [saveTokenForUser]);

  // 로그인 완료 시 보류 토큰 처리 + 토큰 재요청
  useEffect(() => {
    if (!user?.mcode || !isInRnWebView()) return;
    // 보류된 토큰이 있으면 저장
    if (pendingTokenRef.current) {
      const { token, platform } = pendingTokenRef.current;
      saveFcmToken(user.mcode, token, platform).catch(() => {});
      pendingTokenRef.current = null;
    }
    // RN에게 토큰 재요청 (혹시 이미 발급된 토큰을 다시 받기 위해)
    requestPushToken();
  }, [user?.mcode]);

  // PC 웹 푸시 — RN 앱이 아닐 때만 동작
  useEffect(() => {
    if (!user?.mcode || isInRnWebView()) return;
    let cancelled = false;
    (async () => {
      // 백그라운드 푸시(브라우저 권한 허용 시 — 탭 닫혀있을 때용)
      await registerWebPush(user.mcode);
      if (cancelled) return;
    })();
    // 포그라운드 인앱 토스트 + 풀잎 알림음 — 브라우저 푸시 권한과 무관하게 동작
    startNotificationToastListener(user.mcode);
    return () => { cancelled = true; stopNotificationToastListener(); };
  }, [user?.mcode]);

  // 기기별 접속시간 하트비트 — 홈 진입 시 즉시 + 5분마다 기록 (화면크기로 핸드폰/패드/PC 구분)
  useEffect(() => {
    if (!user?.mcode) return;
    const ping = () => reportDeviceAccess(user.mcode, user.name).catch(() => {});
    ping();
    const t = setInterval(ping, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [user?.mcode, user?.name]);

  const [loginTransitionUser, setLoginTransitionUser] = useState(null);

  const login = async (loginId, password, opts = {}) => {
    const result = await verifyLogin(loginId, password);
    if (result.success) {
      // ── IP 접속 게이트 ── 관리자는 IP 무관 허용, 그 외 계정은 허용된 IP(사무실 등)에서만 로그인 가능.
      //  접속 자체를 막지 않고 로그인 시 계정 권한을 보고 판정한다.
      //  (localhost·모바일 앱·IP 조회 실패는 checkIpAccess 내부에서 허용 처리 → 전체 잠김 방지)
      if (!isAdminAccount(result.user)) {
        const ipRes = await checkIpAccess(result.user);
        if (!ipRes.allowed) {
          return {
            success: false,
            error: `허용된 장소(IP)에서만 접속할 수 있습니다.\n관리자에게 IP 등록을 요청해 주세요.${ipRes.ip ? `\n현재 접속 IP: ${ipRes.ip}` : ''}`,
          };
        }
      }
      // 회계 레거시 SSO 자동로그인용 자격증명 보관 (sessionStorage, 탭 닫으면 사라짐)
      try { sessionStorage.setItem('plhouse.acc.cred', JSON.stringify({ u: loginId, p: password })); } catch {}
      // 새 로그인 시 첫 화면은 항상 지도. (새로고침은 마지막 화면 유지하지만, 로그인은 지도로 초기화)
      try { localStorage.setItem('plhouse.viewMode', 'map'); } catch {}
      // 선택적 후크: setUser 전에 실행 (로그인 성공 스플래시 등 UX 용)
      if (typeof opts.onBeforeSet === 'function') {
        try { await opts.onBeforeSet(result.user); } catch {}
      }
      // 홈 화면으로 네비게이션 직후 잠깐 깜빡이는 현상 방지:
      // setUser 전에 전역 스플래시를 띄워두고, 네비 완료 후 덮어씌운 상태로 페이드아웃
      setLoginTransitionUser(result.user);
      setUser(result.user);
      if (!result.user.needPasswordChange) {
        localStorage.setItem(storageKey, JSON.stringify(result.user));
      }
      // 전역 스플래시를 잠시 유지 후 자동 해제
      setTimeout(() => setLoginTransitionUser(null), 800);
    }
    return result;
  };

  const completePasswordChange = () => {
    const updated = { ...user, needPasswordChange: false };
    setUser(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  const logout = async () => {
    // UI 즉시 반응 — 세션 먼저 정리하고 화면 전환
    const uid = user?.mcode;
    const wasWeb = !isInRnWebView();
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ADMIN_STORAGE_KEY);
    try { sessionStorage.removeItem('plhouse.acc.cred'); } catch {} // 회계 SSO 자격증명 정리 (다음 사용자 잔류 방지)
    if (wasWeb) stopNotificationToastListener();

    // 같은 브라우저에서 다른 계정 로그인 시 deleteToken 레이스로 새 토큰까지 무효화되는 문제를 막기 위해,
    // 브라우저 측 토큰(deleteToken)은 건드리지 않고 Firestore 매핑만 정리한다.
    if (uid) {
      removeFcmToken(uid).catch(() => {});
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, completePasswordChange, loginTransitionUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
